import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight, Check } from "lucide-react";
import { useContent } from "@/content/ContentContext";
import { useLang } from "@/i18n/LanguageContext";

const ease = [0.22, 1, 0.36, 1];

export default function Products() {
  const content = useContent();
  const { lang, t } = useLang();
  const products = content.products;
  return (
    <section
      id="range"
      data-testid="products-section"
      className="border-t border-line bg-moss/40"
    >
      <div className="mx-auto max-w-[1440px] px-6 py-28 lg:px-12 lg:py-40">
        <div className="grid items-end gap-10 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <motion.p
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.7, ease }}
              className="text-xs uppercase tracking-[0.3em] text-harvest"
            >
              {t("productsOverline")}
            </motion.p>
            <motion.h2
              initial={{ opacity: 0, y: 32 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.8, delay: 0.1, ease }}
              data-testid="products-heading"
              className="mt-6 font-display text-4xl font-medium leading-[1.05] tracking-tight text-cream sm:text-5xl lg:text-6xl"
            >
              {t("productsHeadA")} <br />
              {t("productsHeadMid")} <span className="italic text-harvest">{t("productsHeadAccent")}</span>
            </motion.h2>
          </div>
          <motion.p
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.15, ease }}
            className="max-w-md text-sm leading-relaxed text-sage sm:text-base lg:col-span-4"
          >
            {t("productsIntro")}
          </motion.p>
        </div>

        <div className="mt-16 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {products.map((p, i) => (
            <motion.article
              key={p.id}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: (i % 3) * 0.08, ease }}
              data-testid={`product-card-${p.id}`}
              className="group flex flex-col border border-line bg-moss transition-transform duration-500 hover:-translate-y-1"
            >
              <Link
                to={`/products/${p.id}`}
                data-testid={`product-link-${p.id}`}
                className="relative block overflow-hidden"
              >
                <img
                  src={p.img}
                  alt={p.alt}
                  className="h-56 w-full object-cover transition-transform duration-700 ease-out group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-black/20 transition-colors duration-500 group-hover:bg-black/5" />
                <span className="absolute left-4 top-4 bg-white/85 px-3 py-1 font-display text-sm italic text-harvest backdrop-blur-md">
                  {p.num}
                </span>
              </Link>
              <div className="flex flex-1 flex-col p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-sage">{p.hindi}</p>
                <Link to={`/products/${p.id}`}>
                  <h3 className="mt-1.5 font-display text-2xl font-medium tracking-tight text-cream transition-colors duration-300 hover:text-harvest">
                    {p.name}
                  </h3>
                </Link>
                <p className="mt-3 flex-1 text-sm leading-relaxed text-sage line-clamp-3">
                  {p.desc}
                </p>
                <div className="mt-5 flex items-center justify-between border-t border-line pt-4">
                  <Link
                    to={`/products/${p.id}`}
                    data-testid={`product-details-${p.id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.15em] text-harvest transition-colors duration-300 hover:text-harvestHover"
                  >
                    View Details <ArrowUpRight className="h-4 w-4" />
                  </Link>
                  <Link
                    to="/contact"
                    data-testid={`product-enquire-${p.id}`}
                    className="text-xs font-semibold uppercase tracking-[0.15em] text-sage transition-colors duration-300 hover:text-harvest"
                  >
                    {t("enquire")}
                  </Link>
                </div>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
